import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";

const FACTOR_LABELS = {
  completion:    { label: "Task Completion",     desc: "Completed / Assigned (90d)" },
  deadline:      { label: "Deadline Discipline", desc: "On-time completions, minus current overdue" },
  complexity:    { label: "Complexity Handled",  desc: "Avg complexity vs department average" },
  approval:      { label: "Approval Success",    desc: "First-pass vs revisions requested" },
  consistency:   { label: "Consistency",         desc: "Stability of weekly output (lower volatility = better)" },
  workload:      { label: "Workload Handling",   desc: "Active task load vs team norm" },
  collaboration: { label: "Collaboration",       desc: "@mention response rate" },
  appreciation:  { label: "Appreciation",        desc: "Recognition received (log-scaled, capped)" },
};

// `color` paints the 48px score and the progress ring; `grad` is the band pill,
// which puts a white label on the gradient. Both were measured and both failed:
//   - score (large text, needs 3:1) failed in LIGHT mode for elite 2.54:1,
//     average 2.15:1 and insufficient 2.56:1;
//   - pill (text-sm, needs 4.5:1) failed on 8 of its 10 stops — worst
//     slate-300 at 1.48:1 and amber-400 at 1.67:1.
// `color` now uses the shared tone inks, measured on both surfaces (>=5.48:1
// light, >=10.72:1 dark). The gradients keep the flourish but start at the -700
// step, so every stop clears 5.02:1 with white. "strong" moves off blue/indigo
// to sky, the hue the info tone already uses.
const BAND_META = {
  elite:             { label: "Elite Performer",  color: "var(--tone-success-fg)", grad: "from-emerald-700 to-emerald-900" },
  strong:            { label: "Strong Performer", color: "var(--tone-info-fg)",    grad: "from-sky-700 to-sky-900" },
  average:           { label: "Average",          color: "var(--tone-warning-fg)", grad: "from-amber-700 to-amber-900" },
  needs_attention:   { label: "Needs Attention",  color: "var(--tone-danger-fg)",  grad: "from-red-700 to-red-900" },
  insufficient_data: { label: "Insufficient data", color: "var(--text-secondary)",  grad: "from-slate-600 to-slate-800" },
};

function ScoreRing({ score, band }) {
  const pct = score / 1000;
  const meta = BAND_META[band] || BAND_META.insufficient_data;
  const stroke = 10;
  const r = 70;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);

  return (
    <div className="relative w-44 h-44">
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={r} stroke="var(--border-subtle)" strokeWidth={stroke} fill="none" />
        <circle
          cx="80" cy="80" r={r}
          stroke={meta.color}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 800ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[10px] uppercase tracking-wider text-ink-400">Efficiency</div>
        <div className="text-5xl font-bold leading-none mt-1" style={{ color: meta.color }}>{score}</div>
        <div className="text-[10px] text-ink-400 mt-0.5">/ 1000</div>
      </div>
    </div>
  );
}

function FactorBar({ name, value, max }) {
  const meta = FACTOR_LABELS[name] || { label: name, desc: "" };
  const pct = max > 0 ? (value / max) * 100 : 0;
  const color = pct >= 80 ? "var(--fill-success)" : pct >= 60 ? "var(--fill-info)" : pct >= 40 ? "var(--fill-warning)" : "var(--fill-danger)";
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <div>
          <span className="text-sm font-semibold text-ink-700">{meta.label}</span>
          <span className="text-[10px] text-ink-400 ml-2">{meta.desc}</span>
        </div>
        <div className="text-xs font-mono text-ink-600">{value}<span className="text-ink-400"> / {max}</span></div>
      </div>
      <div className="w-full h-1.5 bg-ink-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function Sparkline({ history }) {
  if (!history || history.length < 2) {
    return <div className="text-xs text-ink-400 italic">Not enough history yet — chart appears once daily snapshots accumulate.</div>;
  }
  const w = 480, h = 80, pad = 6;
  const xs = history.map((_, i) => pad + (i * (w - 2 * pad)) / (history.length - 1));
  const ys = history.map((p) => h - pad - (p.score / 1000) * (h - 2 * pad));
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x} ${ys[i]}`).join(" ");
  const last = history[history.length - 1].score;
  const first = history[0].score;
  const delta = last - first;
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
        <path d={path} stroke="var(--accent-primary)" strokeWidth="2" fill="none" />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={ys[i]} r="2" fill="var(--accent-primary)" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-ink-500 mt-1">
        <span>{new Date(history[0].computedAt).toLocaleDateString()}</span>
        <span>{delta > 0 ? `▲ +${delta}` : delta < 0 ? `▼ ${delta}` : "no change"}</span>
        <span>{new Date(history[history.length - 1].computedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

export default function EfficiencyPanel({ userId, isSelf, isAdmin }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [eff, h] = await Promise.all([
        api(`/users/${userId}/efficiency`),
        api(`/users/${userId}/efficiency/history?days=60`).catch(() => ({ history: [] })),
      ]);
      setData(eff);
      setHistory(h.history || []);
    } catch (e) {
      setError(e.message || "Failed to load efficiency");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  async function recompute() {
    setRecomputing(true);
    try {
      await api(`/users/${userId}/efficiency/recompute`, { method: "POST" });
      await load();
    } catch (e) { setError(e.message); }
    finally { setRecomputing(false); }
  }

  if (loading) return <div className="bg-white rounded-md shadow-sm p-10 text-center text-sm text-ink-400">Loading efficiency…</div>;
  if (error) return <div className="bg-white rounded-md shadow-sm p-10 text-center text-sm text-red-500">{error}</div>;
  if (!data) return null;

  const eff = data.efficiency || {};
  const factorMax = data.factorMax || {};
  const factors = eff.factors || {};
  const dp = eff.dataPoints || {};
  const meta = BAND_META[eff.band] || BAND_META.insufficient_data;

  return (
    <div className="space-y-4">
      {/* Score header */}
      <div className="bg-white rounded-md shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
          <ScoreRing score={eff.insufficient ? 0 : (eff.score || 0)} band={eff.band || "insufficient_data"} />
          <div className="flex-1 min-w-0">
            <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${meta.grad} text-white text-sm font-semibold`}>
              {meta.label}
            </div>
            {eff.insufficient ? (
              <div className="mt-3">
                <div className="text-sm text-ink-700 font-medium">Not enough data yet to score fairly.</div>
                {(eff.insufficientReasons || []).length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-xs text-ink-500 space-y-0.5">
                    {eff.insufficientReasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-ink-600">
                Based on {dp.assigned} assigned tasks · {dp.completed} completed ·{" "}
                {dp.onTimeOrEarly} on-time ·{" "}
                {dp.appreciations} appreciations · {dp.activeTasks} active right now.
              </p>
            )}
            <div className="mt-3 flex items-center gap-2 text-[11px] text-ink-400">
              <span>Last computed {eff.computedAt ? new Date(eff.computedAt).toLocaleString() : "never"}</span>
              {(isSelf || isAdmin) && (
                <button
                  onClick={recompute}
                  disabled={recomputing}
                  className="ml-2 px-2 py-0.5 rounded border border-ink-200 hover:bg-ink-50 text-ink-600"
                >
                  {recomputing ? "Recomputing..." : "Recompute now"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Factor breakdown */}
      <div className="bg-white rounded-md shadow-sm p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Factor breakdown</h3>
          <div className="text-[10px] text-ink-400">8 factors · weighted to 1000</div>
        </div>
        <div className="space-y-4">
          {Object.keys(factorMax).map((k) => (
            <FactorBar key={k} name={k} value={factors[k] || 0} max={factorMax[k]} />
          ))}
        </div>
      </div>

      {/* Trend */}
      <div className="bg-white rounded-md shadow-sm p-6">
        <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-3">Score trend (last 60 days)</h3>
        <Sparkline history={history} />
      </div>

      {/* Department comparison */}
      {!eff.insufficient && (
        <div className="bg-white rounded-md shadow-sm p-6">
          <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-3">Department context</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Avg complexity" value={dp.avgComplexity?.toFixed?.(1) ?? dp.avgComplexity} sub={`vs dept ${dp.deptAvgComplexity?.toFixed?.(1) ?? dp.deptAvgComplexity}`} />
            <Stat label="Active tasks" value={dp.activeTasks} sub={`vs dept ${dp.deptAvgActive?.toFixed?.(1) ?? dp.deptAvgActive}`} />
            <Stat label="Approvals" value={`${dp.submitted - dp.rejected}/${dp.submitted}`} sub={dp.rejected ? `${dp.rejected} revised` : "no revisions"} />
            <Stat label="@-response" value={`${dp.mentionsResponded}/${dp.mentionsReceived}`} sub="within 48h" />
          </div>
        </div>
      )}

      <div className="bg-blue-50 rounded-md p-4 text-xs text-blue-900">
        <div className="font-semibold mb-1">How this is calculated</div>
        <p>
          Score is computed deterministically from your last 90 days. Factors are weighted: completion 200, deadline 250,
          complexity 150, approval 100, consistency 100, workload 100, collaboration 50, appreciation 50.
          Anti-gaming: tasks closed in &lt;5 minutes count as complexity 1; complexity and workload are normalized to your team average.
          Recomputed nightly + on-demand within seconds of task changes.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-ink-50 rounded-md p-3">
      <div className="text-[10px] text-ink-500 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-bold text-ink-800">{value}</div>
      <div className="text-[10px] text-ink-400 mt-0.5">{sub}</div>
    </div>
  );
}
